import React from "react";
import Seo from "@/components/vlyt/Seo";
import { MapPin, Building2, ShieldCheck, Target, BookOpen } from "lucide-react";

export default function About() {
  return (
    <>
      <Seo title="About · Vlyt Technologies" canonical="https://govlyt.com/about" />

      {/* Header */}
      <section className="pt-[120px] pb-8 mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="text-eyebrow mb-6">The Institution</div>
        <h1 className="text-display font-display max-w-5xl leading-[1.02]">
          Vlyt Technologies: <span className="text-lime">Engineering the Global Standard.</span>
        </h1>
      </section>

      {/* Vision + Mission */}
      <section className="mx-auto max-w-[1440px] px-5 md:px-10 mt-12 grid lg:grid-cols-2 gap-6">
        <article className="rounded-[24px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-8 md:p-12 relative overflow-hidden">
          <div
            className="absolute -top-16 -left-16 h-56 w-56 rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(191,255,0,0.14), transparent 70%)" }}
            aria-hidden="true"
          />
          <div className="relative">
            <div className="flex items-center gap-2 text-eyebrow mb-6">
              <Target size={14} className="text-lime" /> The Vision
            </div>
            <h2 className="text-h2 font-display leading-[1.15]">Our Vision</h2>
            <p className="mt-6 text-[color:var(--vlyt-text)] font-display font-bold text-[19px] md:text-[22px] leading-[1.4] tracking-tight">
              To make <span className="text-lime">accurate thinking</span> the global norm by
              replacing operational noise with evidence-based frameworks for human and
              organizational clarity.
            </p>
          </div>
        </article>

        <article className="rounded-[24px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-8 md:p-12 relative overflow-hidden">
          <div
            className="absolute -bottom-16 -right-16 h-56 w-56 rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(168,123,255,0.18), transparent 70%)" }}
            aria-hidden="true"
          />
          <div className="relative">
            <div className="flex items-center gap-2 text-eyebrow mb-6">
              <ShieldCheck size={14} className="text-[color:var(--vlyt-purple-soft)]" /> The Mission
            </div>
            <h2 className="text-h2 font-display leading-[1.15]">Our Mission</h2>
            <p className="mt-6 text-[color:var(--vlyt-text)] font-display font-bold text-[19px] md:text-[22px] leading-[1.4] tracking-tight">
              Vlyt Technologies builds at the intersection of psychology and technology to
              engineer <span className="text-lime">seamless intra-community ecosystems</span>. We
              empower individuals and organisations to act with intention through proprietary
              systems that automate trust, optimize performance, and move communities forward with
              absolute clarity.
            </p>
          </div>
        </article>
      </section>

      {/* Foundation */}
      <section className="mx-auto max-w-[1440px] px-5 md:px-10 mt-24">
        <div className="rounded-[24px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-2)] p-8 md:p-14 grid lg:grid-cols-[0.9fr_1.4fr] gap-10 items-start">
          <div>
            <div className="flex items-center gap-2 text-eyebrow mb-4">
              <BookOpen size={14} className="text-lime" /> Foundation
            </div>
            <h3 className="text-h2 font-display leading-[1.1]">
              Established 2026. <br />
              <span className="text-lime">Anchored in truth.</span>
            </h3>
            <div className="mt-6 font-mono text-[13px] text-[color:var(--vlyt-purple-soft)] uppercase tracking-[0.2em]">
              Isaiah 29:14a
            </div>
          </div>
          <p className="text-[17px] md:text-[19px] leading-[1.55] text-[color:var(--vlyt-text)]">
            Vlyt was established in 2026 as a commitment to excellence in motion. Anchored in the
            truth of Isaiah 29:14a, we believe that mobility within high-density communities is not
            just a logistical challenge but a moral one. By removing the anxiety of the wait and
            the noise of the negotiate, we free the mental bandwidth of future leaders.
          </p>
        </div>
      </section>

      {/* Institutional Information */}
      <section className="mx-auto max-w-[1440px] px-5 md:px-10 mt-24 mb-16">
        <div className="text-eyebrow mb-8">Institutional Information</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Building2, label: "Registered Entity", value: "Vlyt Technologies Ltd." },
            { icon: MapPin, label: "Headquarters", value: "Port Harcourt, Rivers, Nigeria." },
            { icon: ShieldCheck, label: "Core Logic", value: "Zero-Leakage Architecture: Physical ID Escrow and No-SIM Authentication." },
            { icon: Target, label: "Market Focus", value: "Tertiary Institutions, Gated Residential Estates, and Industrial Hubs." },
          ].map((row) => {
            const Icon = row.icon;
            return (
              <div
                key={row.label}
                className="card-hover rounded-[20px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-6"
              >
                <div className="h-10 w-10 rounded-lg bg-[rgba(191,255,0,0.06)] flex items-center justify-center ring-1 ring-[color:var(--vlyt-border)] mb-5">
                  <Icon size={16} className="text-lime" />
                </div>
                <div className="text-eyebrow mb-2">{row.label}</div>
                <div className="text-[15px] leading-[1.5] text-[color:var(--vlyt-text)]">
                  {row.value}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
